//#region src/patterns.ts
/**
* patterns.ts — 零依赖，纯正则 + 工具函数
*
* 所有语法识别的唯一权威来源 (Single Source of Truth)
* 消费方通过 `@magic-md/core/patterns`（子路径）或 `@magic-md/core`（主入口）import。
*
* ⚠️ 本文件不得 import 任何 npm 包或内部模块。
*/
/** `::define[id]{plugin="name"}` 头部（可含附加属性如 `output="external"`） */
const DEFINE_HEADER_RE = /^::define\[(\w[\w-]*)\]\{([^}]+)\}\s*$/;
/** `::define[id]` — 仅提取 ID（用于 rename / find-references） */
const DEFINE_ID_RE = /^::define\[(\w[\w-]*)\]/;
/** `::` 块闭合（允许尾部空格，与编辑器行为一致） */
const DEFINE_CLOSE_RE = /^::\s*$/;
/** `` ```config `` 或 `` ```output `` 围栏开启。Captures: [1] = backtick sequence, [2] = "config" | "output" */
const FENCE_OPEN_RE = /^(`{3,})(config|output)\s*$/;
/** `` ``` `` 围栏闭合（3+ backticks，无其他内容） */
const FENCE_CLOSE_RE = /^`{3,}\s*$/;
/** `::render[id]::` 或 `::render[ns/.../id]::` 块级（独占一行） */
const BLOCK_RENDER_RE = /^::render\[(\w[\w-]*(?:\/\w[\w-]*)*)\](\{[^}]*\})?::$/;
/** `::render[id]::` 或 `::render[ns/.../id]::` 行内（文本中） */
const INLINE_RENDER_RE = /::render\[(\w[\w-]*(?:\/\w[\w-]*)*)\](\{[^}]*\})?::/g;
/** `::for[loopId]{each="repo" of="$ref{...}" ...}` 开启行 */
const FOR_HEADER_RE = /^::for\[(\w[\w-]*)\]\{(.+)\}\s*$/;
/** `::endfor` 闭合行（允许尾部空格） */
const FOR_CLOSE_RE = /^::endfor\s*$/;
/** `::meta[module]` 或 `::meta[config]` 头部 */
const META_HEADER_RE = /^::meta\[(\w+)\]\s*$/;
/** `::import{from="..." ns="..." ...}` 单行指令（属性值可包含 `$ref{...}`） */
const IMPORT_RE = /^::import\{(.+)\}\s*$/;
/** `::use[id]{from="..." ...}` 头部 */
const USE_HEADER_RE = /^::use\[(\w[\w-]*)\]\{([^}]+)\}\s*$/;
/** `::content[id]` 或 `::content[id]{template="true"}` 头部 — 独立内容指令 */
const CONTENT_HEADER_RE = /^::content\[(\w[\w-]*)\](\{[^}]*\})?\s*$/;
/** `$ref{id.output.path}` — 通用全路径匹配（用于解析器/渲染器） */
const REF_RE = /\$ref\{([^}]+)\}/g;
/** `$ref{id.output.path}` — 全匹配（用于判断整个字符串是否为单个 ref） */
const REF_FULL_RE = /^\$ref\{([^}]+)\}$/;
/** `$ref{id.output...}` / `$ref{id.body}` / `$ref{id.content}` — 提取 definition ID（用于 DAG 依赖构建） */
const REF_ID_RE = /\$ref\{([\w-]+(?:\/[\w-]+)*)\.(?:output|body|content)(?:\.|})/g;
/** `$ref{id.output.path}` / `$ref{id.body}` / `$ref{id.content}` 或 `$ref{ns/id.output.path}` 或 `$ref{ns/__import__/id.output.path}` — 结构化提取 ID + accessor（output|body|content）+ 可选 sub-path（用于 IDE 位置标注） */
const REF_STRUCTURED_RE = /\$ref\{([\w-]+(?:\/[\w-]+)*)\.(output|body|content)(?:\.([^}]+))?\}/g;
/** `$ref{assets/path}` or `$ref{ns/assets/path}` — document/import assets package reference */
const ASSET_REF_RE = /\$ref\{((?:[\w-]+\/)?assets\/[^}]+)\}/g;
/** `$ref{assets/path}` or `$ref{ns/assets/path}` — full match */
const ASSET_REF_FULL_RE = /^\$ref\{((?:[\w-]+\/)?assets\/[^}]+)\}$/;
/** `$ref{unit/id}` or `$ref{unit/ns/id}` — unit identity reference */
const UNIT_IDENTITY_REF_RE = /\$ref\{unit\/([\w-]+(?:\/[\w-]+)*)\}/g;
/** `key="value"` 属性对 */
const ATTR_PAIR_RE = /(\w+)="([^"]*)"/g;
/** `$ref{` 后面（部分输入，triggerCharacter 触发） */
const COMPLETION_REF_TRIGGER_RE = /\$ref\{([^}]*)$/;
/** `plugin="` 后面 */
const COMPLETION_PLUGIN_TRIGGER_RE = /plugin="([^"]*)$/;
/** `::render[` 后面（支持 ns/id） */
const COMPLETION_RENDER_TRIGGER_RE = /::render\[([^\]]*)$/;
/** `::` 开头的指令名（用于指令名补全） */
const COMPLETION_DIRECTIVE_TRIGGER_RE = /^::[a-z]*$/;
/**
* 闭合围栏检测
*
* A closing fence must be exactly `openLength` backticks with no other content
* (matching our convention; CommonMark allows >= but we use exact match for precision).
*/
function isClosingFence(trimmed, openLength) {
	if (trimmed.length !== openLength) return false;
	for (let i = 0; i < openLength; i++) if (trimmed[i] !== "`") return false;
	return true;
}
/**
* 计算安全围栏长度（保证内容中的反引号不会提前闭合围栏）。
* 基于 CommonMark §4.5。返回值 >= 3。
*/
function computeFenceLength(content) {
	let maxRun = 0;
	let currentRun = 0;
	for (let i = 0; i < content.length; i++) if (content[i] === "`") {
		currentRun++;
		if (currentRun > maxRun) maxRun = currentRun;
	} else currentRun = 0;
	return Math.max(3, maxRun + 1);
}
/** 生成指定长度的围栏字符串 */
function makeFence(length) {
	return "`".repeat(length);
}
/** 解析 `{key="val" key2="val2"}` 属性字符串 */
function parseAttrs(attrsStr) {
	if (!attrsStr) return {};
	const attrs = {};
	ATTR_PAIR_RE.lastIndex = 0;
	let m;
	while (m = ATTR_PAIR_RE.exec(attrsStr)) attrs[m[1]] = m[2];
	return attrs;
}
/**
* Parse a `::define[id]{plugin="name" output="external"}` header line.
* Returns null if the line is not a valid define header (requires `plugin` attr).
*/
function parseDefineHeader(line) {
	const m = line.match(DEFINE_HEADER_RE);
	if (!m) return null;
	const attrs = parseAttrs(m[2]);
	if (!attrs.plugin) return null;
	const outputMode = attrs.output === "external" || attrs.output === "inline" ? attrs.output : void 0;
	const configMode = attrs.config === "external" || attrs.config === "inline" ? attrs.config : void 0;
	return {
		id: m[1],
		pluginName: attrs.plugin,
		outputMode,
		configMode,
		template: attrs.template === "true"
	};
}
/**
* 检查行内位置是否在内联代码 span 内（行级版本）。
* 处理多反引号分隔符（`` ` ``、``` `` ```）。
*
* @param line  — 单行文本
* @param posInLine — 在行内的字符偏移
*/
function isInsideCodeSpanInLine(line, posInLine) {
	let i = 0;
	while (i < line.length) if (line[i] === "`") {
		let tickCount = 0;
		const start = i;
		while (i < line.length && line[i] === "`") {
			tickCount++;
			i++;
		}
		const closer = "`".repeat(tickCount);
		let closerPos = i;
		while (true) {
			closerPos = line.indexOf(closer, closerPos);
			if (closerPos === -1) break;
			const afterCloser = closerPos + tickCount;
			if (afterCloser < line.length && line[afterCloser] === "`") {
				closerPos = afterCloser;
				while (closerPos < line.length && line[closerPos] === "`") closerPos++;
				continue;
			}
			if (posInLine > start && posInLine < afterCloser) return true;
			i = afterCloser;
			break;
		}
	} else i++;
	return false;
}
/**
* 检查位置是否在内联代码 span 内（文档级版本）。
* 接受完整文档内容 + 绝对位置，自动定位到所在行后调用 isInsideCodeSpanInLine。
*/
function isInsideInlineCode(content, pos) {
	return isInsideMarkdownCodeSpan(getMarkdownCodeSpanRanges(content), pos);
}
/** 检查位置前是否有反斜杠转义 */
function isEscaped(content, pos) {
	let slashCount = 0;
	for (let i = pos - 1; i >= 0 && content[i] === "\\"; i--) slashCount++;
	return slashCount % 2 === 1;
}
/** 通用 Markdown 围栏开启行：3+ 个反引号或波浪号，后接任意 info string */
const MD_FENCE_OPEN_RE = /^(`{3,}|~{3,})([^`]*)$/;
/**
* 判断一行（trimmed）是否是 Markdown 围栏开启行。
* 注意：CommonMark 规定波浪号围栏的 info string 可以含反引号，但反引号围栏不能。
* 此处采用最常见的"反引号 info 不含反引号"语义，足以覆盖用户指南场景。
*/
function parseMarkdownFenceOpen(trimmed) {
	if (trimmed.length < 3) return null;
	const ch = trimmed[0];
	if (ch !== "`" && ch !== "~") return null;
	let length = 0;
	while (length < trimmed.length && trimmed[length] === ch) length++;
	if (length < 3) return null;
	const info = trimmed.slice(length).trim();
	if (ch === "`" && info.includes("`")) return null;
	return {
		char: ch,
		length,
		info
	};
}
/**
* 判断一行（trimmed）是否是与给定 open 信息匹配的 Markdown 围栏闭合行。
* 闭合规则（CommonMark §4.5）：相同字符，长度 ≥ 开启长度，且不含 info string。
*/
function isMarkdownFenceClose(trimmed, open) {
	if (trimmed.length < open.length) return false;
	for (let i = 0; i < trimmed.length; i++) if (trimmed[i] !== open.char) return false;
	return true;
}
/**
* 扫描整段内容，返回所有 Markdown 围栏代码块所占的字符区间 [start, end)。
* 区间包含围栏行本身（含其换行符）。用于 parseContent 等基于字符位置的扫描。
*/
function getMarkdownFencedRanges(content) {
	const ranges = [];
	const lines = content.split(/\r?\n/);
	const lineStarts = [0];
	for (let offset = 0; offset < content.length; offset++) if (content[offset] === "\n") lineStarts.push(offset + 1);
	let i = 0;
	while (i < lines.length) {
		const open = parseMarkdownFenceOpen(lines[i].trim());
		if (open) {
			const start = lineStarts[i];
			let j = i + 1;
			while (j < lines.length) {
				if (isMarkdownFenceClose(lines[j].trim(), open)) break;
				j++;
			}
			const endLineIdx = j < lines.length ? j : lines.length - 1;
			const endPos = lineStarts[endLineIdx] + lines[endLineIdx].length;
			ranges.push([start, endPos]);
			i = j + 1;
			continue;
		}
		i++;
	}
	return ranges;
}
/** 判断字符位置是否落在任一 Markdown 围栏代码块区间内 */
function isInsideMarkdownFencedBlock(ranges, pos) {
	let low = 0;
	let high = ranges.length - 1;
	while (low <= high) {
		const mid = Math.floor((low + high) / 2);
		const [start, end] = ranges[mid];
		if (pos < start) high = mid - 1;
		else if (pos >= end) low = mid + 1;
		else return true;
	}
	return false;
}
/**
* Return CommonMark-style inline code span ranges, including spans whose
* matching backtick delimiters are on different lines. Fenced code blocks are
* opaque and never contribute inline spans.
*/
function getMarkdownCodeSpanRanges(content) {
	const ranges = [];
	const fencedRanges = getMarkdownFencedRanges(content);
	let fencedIndex = 0;
	let i = 0;
	while (i < content.length) {
		const fenced = fencedRanges[fencedIndex];
		if (fenced && i >= fenced[1]) {
			fencedIndex++;
			continue;
		}
		if (fenced && i >= fenced[0]) {
			i = fenced[1];
			fencedIndex++;
			continue;
		}
		if (content[i] !== "`" || isEscaped(content, i)) {
			i++;
			continue;
		}
		const start = i;
		while (i < content.length && content[i] === "`") i++;
		const delimiterLength = i - start;
		let cursor = i;
		let closed = false;
		while (cursor < content.length) {
			const next = content.indexOf("`", cursor);
			if (next < 0) break;
			if (isEscaped(content, next)) {
				cursor = next + 1;
				continue;
			}
			let end = next;
			while (end < content.length && content[end] === "`") end++;
			if (end - next === delimiterLength) {
				ranges.push([start, end]);
				i = end;
				closed = true;
				break;
			}
			cursor = end;
		}
		if (!closed) i = start + delimiterLength;
	}
	return ranges;
}
function isInsideMarkdownCodeSpan(ranges, pos) {
	return isInsideMarkdownFencedBlock(ranges, pos);
}
//#endregion
export { ASSET_REF_FULL_RE, ASSET_REF_RE, ATTR_PAIR_RE, BLOCK_RENDER_RE, COMPLETION_DIRECTIVE_TRIGGER_RE, COMPLETION_PLUGIN_TRIGGER_RE, COMPLETION_REF_TRIGGER_RE, COMPLETION_RENDER_TRIGGER_RE, CONTENT_HEADER_RE, DEFINE_CLOSE_RE, DEFINE_HEADER_RE, DEFINE_ID_RE, FENCE_CLOSE_RE, FENCE_OPEN_RE, FOR_CLOSE_RE, FOR_HEADER_RE, IMPORT_RE, INLINE_RENDER_RE, MD_FENCE_OPEN_RE, META_HEADER_RE, REF_FULL_RE, REF_ID_RE, REF_RE, REF_STRUCTURED_RE, UNIT_IDENTITY_REF_RE, USE_HEADER_RE, computeFenceLength, getMarkdownCodeSpanRanges, getMarkdownFencedRanges, isClosingFence, isEscaped, isInsideCodeSpanInLine, isInsideInlineCode, isInsideMarkdownCodeSpan, isInsideMarkdownFencedBlock, isMarkdownFenceClose, makeFence, parseAttrs, parseDefineHeader, parseMarkdownFenceOpen };
